import { type ComponentType } from 'react'
import { getPhotoBookProps } from 'services/Analytics/photoBooksAnalytics'
import { DEFAULT_BOOK_SIZE } from 'services/PhotoBook/PhotoBook.consts'
import { usePhotoBooksStore } from 'stores/RootStoreProvider'
import { setBookQuantity } from 'components/PhotoBook/PhotoBookProvider/PhotoBook.updaters'
import { readOutsideReaction } from 'utils/mobx/readOutsideReaction'
import { useMobxSelector } from 'utils/mobx/useMobxSelector'

export type PhotoBookSummaryData = {
  photosCount: number
  analyticsProps: Record<string, unknown>
  setPhotoBooksQuantity: (quantity: number) => void
}

/**
 * Supplies the single-product checkout order summary with the book it orders,
 * which is the same book the editor shows.
 * Selects the derived values rather than `cover`/`pages` themselves: the
 * selector snapshots what it returns, and a full book is a large tree to clone
 * and structurally compare on every change.
 */
export function withPhotoBookSummaryData<P extends object>(
  Component: ComponentType<P & { photoBook: PhotoBookSummaryData }>
): ComponentType<P> {
  return function WithPhotoBookSummaryData(props: P) {
    const photoBooksStore = usePhotoBooksStore()
    const { photosCount, analyticsProps } = useMobxSelector(() => {
      const bookStore = photoBooksStore.bookToShow
      const book = bookStore?.bookData
      return {
        photosCount: bookStore?.photosCount ?? 0,
        analyticsProps: getPhotoBookProps(
          bookStore?.bookId ?? null,
          book?.size ?? DEFAULT_BOOK_SIZE,
          book?.cover ?? null,
          book?.pages ?? []
        ),
      }
    }, [photoBooksStore])

    return (
      <Component
        {...props}
        photoBook={{
          photosCount,
          analyticsProps,
          setPhotoBooksQuantity: (quantity: number) =>
            readOutsideReaction(() => photoBooksStore.bookToShow)?.updateBook(
              setBookQuantity(quantity)
            ),
        }}
      />
    )
  }
}
